import { createStore } from "redux";

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AnyAction,
  combineRootReducerWithScope,
  ReducerWithScope,
  RootReducerFromScopedReducer,
} from "@gamedev-web/gamedev-sdk-core";

import type { PreloadedState, Reducer, Store, StoreEnhancer } from "redux";
type ReducersMapObjectWithScope<S, A extends AnyAction> = {
  [K in keyof S]: ReducerWithScope<Reducer<S[K], A>>;
};

export type StoreWithInjectReducer<StoreType> = StoreType extends Store
  ? StoreType & {
      injectReducer: (
        partnerId: string,
        reducer: ReducerWithScope<Reducer<any, AnyAction>>
      ) => void;
    }
  : never;

type ActionFromReducer<R> = R extends Reducer<any, infer A> ? A : never;

type InternalStoreWithInjectedReducer<StoreType> = StoreWithInjectReducer<StoreType> & {
  asyncReducers: Record<string, ReducerWithScope<Reducer<any, AnyAction>>>;
};

export function createInjectableStore<
  MapType extends ReducersMapObjectWithScope<any, any>,
  Ext,
  StateExt,
>(
  reducersMap: MapType,
  preloadedState?: PreloadedState<ReturnType<RootReducerFromScopedReducer<MapType>>>,
  enhancer?: StoreEnhancer<Ext, StateExt>
): StoreWithInjectReducer<
  Store<
    ReturnType<RootReducerFromScopedReducer<MapType>> & StateExt,
    ActionFromReducer<RootReducerFromScopedReducer<MapType>>
  > &
    Ext
> {
  const staticReducers = reducersMap;
  const rootReducer = combineRootReducerWithScope(staticReducers);

  const store = createStore(rootReducer, preloadedState, enhancer);
  const typedStore = store as InternalStoreWithInjectedReducer<typeof store>;
  typedStore.asyncReducers = {};

  const injectReducer = (partnerId: string, reducer: ReducerWithScope<Reducer<any, AnyAction>>) => {
    typedStore.asyncReducers[partnerId] = reducer;

    const newReducer = combineRootReducerWithScope({
      ...staticReducers,
      ...typedStore.asyncReducers,
    });

    typedStore.replaceReducer(newReducer);
  };

  typedStore.injectReducer = injectReducer.bind(typedStore);

  return typedStore;
}
